import React, { useContext } from 'react';
import styled from 'styled-components';

import Icon from '../../misc/Icon';
import Button from '../../misc/Button';
import InputSettingsField from './InputSettingsField';
import { getFieldInputType, getDisplayValue, getChangeValue } from '../fields/TextField';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import ConditionalLogic from '../../../constants/ConditionalLogic';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldTypes from '../../../constants/FieldTypes';

import { sortFieldPositionsByXAndYCoordinates } from '../../../lib/documentHelper';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledStepLabel = styled.div`
  font-weight: 500;
  margin-left: 6px;
  font-size: 14px;
  color: ${(props) => props.theme.fontColorPrimary};
`;

const StyledSectionLabel = styled.div`
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const StyledConditionColumn = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => (props.conditions && props.conditions.length > 1
    ? 'max-height: 450px; overflow-y: auto;'
    : '')};
`;

const StyledConditionRow = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 8px;
  flex-direction: row;
  align-items: center;
`;

const StyledConditionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 8px;
  margin-bottom: 8px;
  padding-bottom: 2px;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background-color: ${(props) => props.theme.secondaryColor};
`;

const StyledConditionSelectWrapper = styled.div`
  display: flex;
`;

const StyledValueNodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 4px;
`;

const StyledConditionDelete = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
`;

export const CollectionConditionalLogicSettingsForm = ({
  targetLabel,
  activeSchemaTable,
  schema,
  logic,
  onChange,
  sortedPages,
  fields,
  fileId,
}) => {
  const theme = useContext(ThemeContext);
  const { parentId } = activeSchemaTable;

  const formattedSortedPages = (sortedPages || [])
    .map((page, i) => ({ ...page, label: `Page ${i + 1}` }));

  const fieldLookup = {};
  (fields || []).forEach((f) => fieldLookup[f._id] = f);

  const handleChange = (key, value) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    nextLogic[key] = value;
    if (onChange) onChange(nextLogic);
  };

  const handleColumnConditionCreate = () => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (nextLogic.conditions) {
      nextLogic.conditions.push({ schema: parentId });
    } else {
      nextLogic.conditions = [{ schema: parentId }];
    }
    onChange(nextLogic);
  };

  const handlePageConditionCreate = () => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (nextLogic.conditions) {
      nextLogic.conditions.push({ file: fileId });
    } else {
      nextLogic.conditions = [{ file: fileId }];
    }
    onChange(nextLogic);
  };

  const handleConditionChange = (index, changes) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (!nextLogic.conditions[index]) return;
    nextLogic.conditions[index] = {
      ...nextLogic.conditions[index],
      ...changes
    };
    onChange(nextLogic);
  };

  const handleConditionDelete = (index) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    nextLogic.conditions.splice(index, 1);
    onChange(nextLogic);
  };

  const indexedConditions = (logic?.conditions || []).map((c, i) => ({ ...c, _originalIndex: i }));
  const columnConditions = indexedConditions.filter(c => c.schema !== undefined);
  const pageConditions = indexedConditions.filter(c => c.file !== undefined);

  return (
    <StyledWrapper>
      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            value={logic.action}
            options={[
              { _id: 'show', value: 'Show' },
              { _id: 'hide', value: 'Hide' }
            ]}
            onChange={(i, value) => handleChange('action', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>
          this
          {' '}
          {targetLabel}
          {' '}
          when
        </StyledStepLabel>
      </StyledConditionRow>

      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            inputStyles={{ maxWidth: '100px' }}
            value={logic.eval}
            options={[
              { _id: 'and', value: 'All' },
              { _id: 'or', value: 'Any' }
            ]}
            onChange={(i, value) => handleChange('eval', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>of the following conditions match</StyledStepLabel>
      </StyledConditionRow>

      <StyledSectionLabel theme={theme}>Column</StyledSectionLabel>
      <StyledConditionRow style={{ marginBottom: 0 }}>
        <StyledConditionColumn style={{ flex: 1 }} conditions={columnConditions}>
          {columnConditions.map((condition) => (
            <ColumnLogicCondition
              key={condition._originalIndex}
              index={condition._originalIndex}
              column={condition.column}
              condition={condition.condition}
              value={condition.value}
              schema={schema}
              activeSchemaTable={activeSchemaTable}
              onChange={handleConditionChange}
              onDelete={handleConditionDelete}
            />
          ))}
        </StyledConditionColumn>
      </StyledConditionRow>

      <StyledConditionRow>
        <Button icon={ThemeIcons.misc.add} onClick={handleColumnConditionCreate} />
      </StyledConditionRow>

      <StyledSectionLabel theme={theme}>Page</StyledSectionLabel>
      <StyledConditionRow style={{ marginBottom: 0 }}>
        <StyledConditionColumn style={{ flex: 1 }} conditions={pageConditions}>
          {pageConditions.map((condition) => (
            <PageLogicCondition
              key={condition._originalIndex}
              index={condition._originalIndex}
              file={condition.file}
              page={condition.page}
              field={condition.field}
              condition={condition.condition}
              value={condition.value}
              fieldLookup={fieldLookup}
              pages={formattedSortedPages}
              onChange={handleConditionChange}
              onDelete={handleConditionDelete}
            />
          ))}
        </StyledConditionColumn>
      </StyledConditionRow>

      <StyledConditionRow>
        <Button icon={ThemeIcons.misc.add} onClick={handlePageConditionCreate} />
      </StyledConditionRow>
    </StyledWrapper>
  );
};

export const ColumnLogicCondition = ({
  index,
  schema,
  activeSchemaTable,
  column,
  condition,
  value,
  onChange,
  onDelete
}) => {
  const theme = useContext(ThemeContext);

  const parentTable = schema[activeSchemaTable.parentId];

  const columnDefinitions = parentTable.tableColumns.filter((col) => [
    FieldTableColumnTypes.text,
    FieldTableColumnTypes.number,
    FieldTableColumnTypes.dropdown,
    FieldTableColumnTypes.multiSelect,
    FieldTableColumnTypes.barcode,
  ].includes(col.type));

  const parentColumnOptions = columnDefinitions.map((col) => ({
    _id: col._id,
    value: col.title || col.identifier || col._id,
  }));

  const selectedColumn = columnDefinitions.find((col) => col._id === column);

  let conditionOptions = [];
  let valueNode = null;

  if (selectedColumn) {
    switch (selectedColumn.type) {
      case FieldTableColumnTypes.text:
      case FieldTableColumnTypes.barcode:
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
          { _id: ConditionalLogic.conditions.contain, value: 'contains' },
        ];
        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="text"
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;

      case FieldTableColumnTypes.number:
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
          { _id: ConditionalLogic.conditions.greaterThan, value: 'is greater than' },
          { _id: ConditionalLogic.conditions.lessThan, value: 'is less than' },
        ];
        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="number"
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;

      case FieldTableColumnTypes.dropdown:
      case FieldTableColumnTypes.multiSelect: {
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
        ];

        const optionItems = (selectedColumn.options || [])
          .filter((opt) => !opt.deleted)
          .map((opt) => ({ _id: opt._id, value: opt.value }));

        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="dropdown"
            options={optionItems}
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;
      }

      default:
        break;
    }
  }

  const showValueField =
    selectedColumn &&
    ![ConditionalLogic.conditions.filled, ConditionalLogic.conditions.empty].includes(condition);

  return (
    <StyledConditionWrapper theme={theme}>
      <StyledConditionColumn style={{ flex: 1 }}>
        <StyledConditionRow>
          <InputSettingsField
            label="Column"
            value={column}
            type="dropdown"
            options={parentColumnOptions}
            onChange={(i, columnId) => onChange(index, { column: columnId })}
          />
        </StyledConditionRow>

        {selectedColumn && (
          <StyledConditionRow>
            <InputSettingsField
              label="Condition"
              value={condition}
              type="dropdown"
              options={conditionOptions}
              onChange={(i, val) => onChange(index, { condition: val })}
            />
          </StyledConditionRow>
        )}

        {selectedColumn && condition && showValueField && (
          <StyledConditionRow>
            <StyledConditionSelectWrapper>{valueNode}</StyledConditionSelectWrapper>
          </StyledConditionRow>
        )}
      </StyledConditionColumn>

      <StyledConditionColumn>
        <StyledConditionDelete>
          <Icon
            color={theme.dangerColor}
            icon={ThemeIcons.settings.delete}
            size="sm"
            onClick={() => onDelete(index)}
          />
        </StyledConditionDelete>
      </StyledConditionColumn>
    </StyledConditionWrapper>
  );
};

export const PageLogicCondition = ({
  index,
  fieldLookup,
  pages,
  file,
  page,
  field,
  condition,
  value,
  onChange,
  onDelete,
}) => {
  const theme = useContext(ThemeContext);

  const handleChange = (key, val) => {
    const nextForm = { file, page, field, condition, value, [key]: val };

    if (key === 'page') {
      delete nextForm.field;
      delete nextForm.value;
    } else if (key === 'field') {
      delete nextForm.value;
    }

    if (onChange) onChange(index, nextForm);
  };

  const selectedPage = !page ? null : pages.find((p) => p._id === page);

  const selectedPageFieldsLookup = {};
  const selectedPageFields = !selectedPage ? null : [];

  if (selectedPage) {
    const sortedFieldPositions = sortFieldPositionsByXAndYCoordinates(selectedPage.fieldPositions);

    sortedFieldPositions.forEach((fieldPosition) => {
      if (
        fieldPosition.type !== FieldTypes.text
        && fieldPosition.type !== FieldTypes.textarea
        && fieldPosition.type !== FieldTypes.number
        && fieldPosition.type !== FieldTypes.dropdown
        && fieldPosition.type !== FieldTypes.multiSelect
      ) return;

      if (!selectedPageFieldsLookup[fieldPosition.field]) {
        selectedPageFields.push(fieldLookup[fieldPosition.field]);
        selectedPageFieldsLookup[fieldPosition.field] = true;
      }
    });
  }

  const selectedField = !field ? null : fieldLookup[field];

  const pageOptions = pages?.map((p) => {
    return { _id: p._id, value: p.label };
  });

  const fieldOptions = selectedPageFields?.map((f) => {
    return { _id: f._id, value: f.title || f.identifier || f.type };
  });

  let conditionOptions = [];
  let valueNode = null;

  if (
    selectedField &&
    (selectedField.type === FieldTypes.multiSelect || selectedField.type === FieldTypes.dropdown)
  ) {
    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
    ];

    const selectOptions = [];
    if (selectedField && selectedField.options) {
      selectedField.options.forEach((opt) => {
        if (!opt.deleted) selectOptions.push({ _id: opt._id, value: opt.value });
      });
    }

    valueNode = (
      <InputSettingsField
        value={value}
        type="dropdown"
        options={selectOptions}
        onChange={(i, val) => handleChange('value', val)}
      />
    );
  } else if (
    selectedField &&
    selectedField.type === FieldTypes.number
  ) {
    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
      { _id: ConditionalLogic.conditions.greaterThan, value: 'is greater than' },
      { _id: ConditionalLogic.conditions.lessThan, value: 'is less than' },
    ];

    valueNode = (
      <InputSettingsField
        type={getFieldInputType(false, selectedField.type, selectedField.format)}
        value={getDisplayValue(selectedField.type, value, selectedField.format)}
        onChange={(i, val) => handleChange('value', getChangeValue(selectedField.type, val))}
      />
    );
  } else if (
    selectedField &&
    (selectedField.type === FieldTypes.text || selectedField.type === FieldTypes.textarea)
  ) {
    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
      { _id: ConditionalLogic.conditions.contain, value: 'contains' },
    ];

    valueNode = (
      <InputSettingsField
        type={getFieldInputType(false, selectedField.type, selectedField.format)}
        value={getDisplayValue(selectedField.type, value, selectedField.format)}
        onChange={(i, val) => handleChange('value', getChangeValue(selectedField.type, val))}
      />
    );
  }

  return (
    <StyledConditionWrapper theme={theme}>
      <StyledConditionColumn style={{ flex: 1 }}>
        <StyledConditionRow>
          <InputSettingsField
            label="Page"
            value={page}
            type="dropdown"
            options={pageOptions}
            onChange={(i, pageId) => handleChange('page', pageId)}
          />
        </StyledConditionRow>
        {!selectedPage ? null : (
          <StyledConditionRow>
            <InputSettingsField
              label="Field"
              value={field}
              type="dropdown"
              options={fieldOptions}
              onChange={(i, val) => handleChange('field', val)}
            />
          </StyledConditionRow>
        )}
        {!selectedPage || !selectedField ? null : (
          <StyledConditionRow>
            <StyledConditionSelectWrapper>
              <InputSettingsField
                value={condition}
                type="dropdown"
                options={conditionOptions}
                onChange={(i, val) => handleChange('condition', val)}
              />
            </StyledConditionSelectWrapper>
            <StyledValueNodeWrapper>
              {condition === ConditionalLogic.conditions.empty || condition === ConditionalLogic.conditions.filled ? null : valueNode}
            </StyledValueNodeWrapper>
          </StyledConditionRow>
        )}
      </StyledConditionColumn>
      <StyledConditionColumn>
        <StyledConditionDelete>
          <Icon
            color={theme.dangerColor}
            icon={ThemeIcons.settings.delete}
            size="sm"
            onClick={() => onDelete(index)}
          />
        </StyledConditionDelete>
      </StyledConditionColumn>
    </StyledConditionWrapper>
  );
};

export default CollectionConditionalLogicSettingsForm;
